import React, { useCallback } from "react";

import { graphql } from "gatsby";

import LogoCapital from "cms/CompanyLogos/logo-capital-one.png";
import LogoCognizant from "cms/CompanyLogos/logo-cognizant.png";
import LogoExperian from "cms/CompanyLogos/logo-experian.png";
import LogoFacebook from "cms/CompanyLogos/logo-facebook.png";
import LogoHitachi from "cms/CompanyLogos/logo-hitachi-solutions.png";
import LogoNtt from "cms/CompanyLogos/logo-ntt-data.png";
import Button from "cms/components/Button";
import Text from "cms/components/Text";

import useEnterpriseFormBlockService from "./EnterpriseFormBlock.service";
import {
  Container,
  Content,
  Error,
  FieldGroup,
  FieldGroupContainer,
  Form,
  Illustration,
  IllustrationContainer,
  IllustrationWrapper,
  Input,
  Label,
  SuccessContainer,
  SuccessDescription,
  Tagline,
} from "./EnterpriseFormBlock.styles";

export interface EnterpriseFormBlockProps
  extends Queries.SanityEnterpriseFormBlock {}

const COMPANY_LOGOS = [
  {
    image: LogoCapital,
    imageAlt: "Capital One logo",
  },
  {
    image: LogoCognizant,
    imageAlt: "Cognizant logo",
  },
  {
    image: LogoHitachi,
    imageAlt: "Hitachi Solutions logo",
  },
  {
    image: LogoExperian,
    imageAlt: "Experian logo",
  },
  {
    image: LogoFacebook,
    imageAlt: "Facebook logo",
  },
  {
    image: LogoNtt,
    imageAlt: "NTT Data logo",
  },
] as const;

type Field = {
  label: string;
  name: string;
  value: string;
  error?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const EnterpriseFormBlock: React.FC<EnterpriseFormBlockProps> = ({
  headline,
  tagline,
}) => {
  const {
    isLoading,
    isFormSuccessful,
    inputs,
    errors,
    onSubmit,
    onValueChange,
  } = useEnterpriseFormBlockService();

  const onFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      onSubmit(event);
    },
    [onSubmit],
  );

  const onFirstNameInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange("firstName", event.target.value);
    },
    [onValueChange],
  );

  const onLastNameInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange("lastName", event.target.value);
    },
    [onValueChange],
  );

  const onEmailInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange("email", event.target.value);
    },
    [onValueChange],
  );

  const onCompanyNameInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange("companyName", event.target.value);
    },
    [onValueChange],
  );

  const onCompanySizeInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange("companySize", event.target.value);
    },
    [onValueChange],
  );

  const onJobTitleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange("jobTitle", event.target.value);
    },
    [onValueChange],
  );

  const FIELDS: Field[] = [
    {
      label: "First Name",
      name: "firstName",
      value: inputs.firstName,
      onChange: onFirstNameInputChange,
    },
    {
      label: "Last Name",
      name: "lastName",
      value: inputs.lastName,
      onChange: onLastNameInputChange,
    },
    {
      label: "Email",
      name: "email",
      value: inputs.email,
      error: errors.email?.join(", "),
      onChange: onEmailInputChange,
    },
    {
      label: "Company Name",
      name: "companyName",
      value: inputs.companyName,
      onChange: onCompanyNameInputChange,
    },
    {
      label: "Company Size",
      name: "companySize",
      value: inputs.companySize,
      onChange: onCompanySizeInputChange,
    },
    {
      label: "Job Title",
      name: "jobTitle",
      value: inputs.jobTitle,
      onChange: onJobTitleInputChange,
    },
  ];

  return (
    <Container>
      <Content>
        <Tagline>{tagline}</Tagline>
        <IllustrationWrapper>
          {COMPANY_LOGOS?.map((companyLogo) => (
            <IllustrationContainer key={companyLogo.imageAlt}>
              <Illustration
                src={companyLogo.image}
                alt={companyLogo.imageAlt}
              />
            </IllustrationContainer>
          ))}
        </IllustrationWrapper>
        <Text variant="H3" as="h3" textAlign="center">
          {headline}
        </Text>
        {!isFormSuccessful && (
          <Form onSubmit={onFormSubmit}>
            <FieldGroupContainer>
              {FIELDS.map((field) => (
                <FieldGroup key={field.label}>
                  <Label>{field.label}</Label>
                  <Input
                    type="text"
                    name={field.name}
                    aria-label={field.label}
                    value={field.value}
                    onChange={field.onChange}
                  />

                  {field.error && <Error>{field.error}</Error>}
                </FieldGroup>
              ))}
            </FieldGroupContainer>
            <Button
              type="submit"
              disabled={isLoading}
              variant="secondary"
              size="large"
              label={isLoading ? "Contacting sales..." : "Contact sales"}
              fullWidth
            />
            {/* {isLoading ? 'Contacting sales...' : 'Contact sales'}
            </Button> */}

            {errors.general.length > 0 && (
              <Error>{errors.general.join(", ")}</Error>
            )}
          </Form>
        )}

        {isFormSuccessful && (
          <SuccessContainer>
            <Text variant="H3" as="h3" textAlign="center">
              Thanks you
            </Text>
            <SuccessDescription>
              We have received your message.
            </SuccessDescription>
          </SuccessContainer>
        )}
      </Content>
    </Container>
  );
};

export const query = graphql`
  fragment EnterpriseFormBlock on SanityEnterpriseFormBlock {
    _key
    _type
    tagline
    headline
  }
`;

export default EnterpriseFormBlock;
